import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, Phone, CalendarDays } from "lucide-react";
import { PHONE_TEL, BOOK_URL, scrollToId } from "../lib/site";
import { HeroFrame } from "./HeroFrame";

const LINES = [
  { t: "Coverage that", c: "" },
  { t: "holds steady", c: "italic text-[#E5A855]" },
  { t: "when life doesn't.", c: "" },
];
const ease = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative overflow-hidden bg-[#071510] text-[#FAF8F5] pt-32 lg:pt-40 pb-20 lg:pb-28">
      <div className="absolute -top-40 right-[-10%] w-[620px] h-[620px] rounded-full bg-[#C88A35]/20 blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="font-mono-d text-xs uppercase tracking-[0.3em] text-[#E5A855] mb-8 flex items-center gap-3">
            <span className="w-10 h-px bg-[#E5A855]" /> Licensed Life &amp; Health Agent · Nationwide
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif-d font-medium tracking-tight leading-[0.98] text-[3.1rem] sm:text-7xl lg:text-[6.4rem]">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em]">
                <motion.span className={`block ${l.c}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.15, delay: 0.25 + i * 0.14, ease }}>
                  {l.t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="mt-8 max-w-xl text-base md:text-lg text-[#F4F1EA]/80 leading-relaxed">
            ACA &amp; private health plans, dental, vision and hearing, life insurance, college savings and retirement — explained plainly and matched to your budget by one dedicated agent.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }} className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("quote")} data-testid="hero-get-quote-btn" className="group inline-flex items-center gap-3 rounded-full bg-[#E5A855] text-[#071510] pl-7 pr-2 py-2 font-semibold hover:bg-[#FAF8F5] transition-colors">
              Get my free quote
              <span className="w-10 h-10 rounded-full bg-[#071510] text-[#E5A855] grid place-items-center group-hover:rotate-45 transition-transform duration-500"><ArrowUpRight className="w-5 h-5" /></span>
            </button>
            <a href={`tel:${PHONE_TEL}`} data-testid="hero-call-btn" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-4 text-sm hover:bg-white/10 transition-colors">
              <Phone className="w-4 h-4" /> Call now
            </a>
            <a href={BOOK_URL} target="_blank" rel="noreferrer" data-testid="hero-book-btn" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-4 text-sm hover:bg-white/10 transition-colors">
              <CalendarDays className="w-4 h-4" /> Book a call
            </a>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <HeroFrame rx={rx} ry={ry} />
        </div>
      </div>
    </section>
  );
};
